<template>
  <div class="production-management-page">
    <ProductionHeader @back="goBack" />

    <CompetenceFilter
      v-model="selectedDate"
      :last-update-date="lastUpdateDate"
      :max-date="maxDate"
      :is-loading="loadingPeriod"
      @change="onCompetenceChange"
      @refresh="onCompetenceChange(selectedDate)"
    />

    <div class="demonstrativos-container">
      <ProductionSectionGroup
        v-for="section in productionSections"
        :key="section.key"
        v-bind="section.props"
        @generate-statement="openStatementModal(section)"
        @upload-invoice="openInvoiceUploadModal(section)"
        @open-backbeat="openBackbeat"
        @open-glosas="openGlosas"
      />

      <div v-if="!loadingPeriod && productionSections.length === 0" class="empty-state">
        <p>Não há dados de produção para a competência selecionada.</p>
      </div>
    </div>

    <div class="divider-line my-4"></div>

    <ProductionHistory
      :competence-date="selectedDate"
      @download-statement="handleDownloadStatement"
      @download-invoice="handleDownloadInvoice"
      @delete-invoice="handleDeleteInvoice"
    />

    <StatementDetailModal
      v-model="isStatementModalOpen"
      :modal-data="selectedStatementData"
      @download="handleDownloadStatement"
    />

    <InvoiceUploadModal
      v-model="isInvoiceModalOpen"
      :modal-data="selectedInvoiceData"
      @upload-success="handleInvoiceUploadSuccess"
    />
  </div>
</template>

<script>
import moment from "moment";
import { inject } from "@vue/composition-api";
import ProductionHeader from "../components/ProductionHeader.vue";
import CompetenceFilter from "../components/CompetenceFilter.vue";
import ProductionSectionGroup from "../components/ProductionSectionGroup.vue";
import ProductionHistory from "../components/ProductionHistory.vue";
import StatementDetailModal from "../modal/StatementDetailModal.vue";
import InvoiceUploadModal from "../modal/InvoiceUploadModal.vue";
import {
  getCompetences,
  getAnalyticsAfterSelectingPeriod,
} from "../../../services/analytics/analytics-services.js";
import { getMonthNumber } from "../../../utils/date/date-utils";
import { showBackbeat } from "../../../services/backbeat/route-page";
import RoutesPaths from "../../../router/router-structure/routes-paths";

const NO_INVOICE_TEXT = "Não é necessário nota fiscal para este item.";

export default {
  name: "ProductionManagement",
  components: {
    ProductionHeader,
    CompetenceFilter,
    ProductionSectionGroup,
    ProductionHistory,
    StatementDetailModal,
    InvoiceUploadModal,
  },
  setup() {
    const globalLoading = inject("globalLoading", null);
    const globalShowInfoError = inject("globalShowInfoError", null);
    return { globalLoading, globalShowInfoError };
  },
  data() {
    return {
      selectedDate: null,
      lastUpdateDate: null,
      maxDate: null,
      loadingPeriod: false,
      isInitializing: false,

      physicalPerson: null,
      juridicalPerson: [],
      transfer: null,

      isStatementModalOpen: false,
      selectedStatementData: {},
      isInvoiceModalOpen: false,
      selectedInvoiceData: {},
    };
  },
  computed: {
    year() {
      return this.toMoment(this.selectedDate)?.format("YYYY") || null;
    },
    month() {
      return this.toMoment(this.selectedDate)?.format("MM") || null;
    },

    /**
     * Fonte única de verdade da tela: cada item já vem pronto no formato
     * que o <ProductionSectionGroup> espera (props), mais os dados que
     * os modais de demonstrativo / envio de NF vão usar quando o card
     * for clicado. Adicionar um novo tipo de produção = adicionar um
     * bloco aqui, sem mexer no template.
     */
    productionSections() {
      const sections = [];

      if (this.hasMainData(this.physicalPerson)) {
        sections.push(
          this.buildMainSection({
            key: "PF",
            title: "Produção Médica PF",
            subtitle: this.physicalPerson.descriptionProduction,
            entry: this.physicalPerson,
            hasUploadInvoice: true,
          })
        );
      }

      if (this.hasMainData(this.transfer)) {
        sections.push(
          this.buildMainSection({
            key: "REPASSE",
            title: "Produção Repasse",
            cardTitle: "Produção de Repasse",
            subtitle: this.transfer.descriptionProduction,
            entry: this.transfer,
            hasUploadInvoice: true,
          })
        );
      }

      this.juridicalPerson.forEach((pj, index) => {
        if (!this.hasMainData(pj, ["amountReceivable", "invoiceValue"])) return;
        sections.push(
          this.buildMainSection({
            key: `PJ-${index}`,
            title: "Produção Médica PJ",
            subtitle: pj.descriptionProduction,
            entry: pj,
            hasUploadInvoice: false,
            infoText: "Pagamento direcionado ao prestador.",
            preferAmountReceivable: true,
          })
        );
      });

      if (this.hasSimpleValue(this.physicalPerson?.talk)) {
        sections.push(
          this.buildSimpleSection({
            key: "TALK",
            title: "Palestras",
            entry: this.physicalPerson.talk,
          })
        );
      }

      if (this.hasMeetingsData) {
        sections.push(
          this.buildSimpleSection({
            key: "REUNIOES",
            title: "Reuniões",
            icon: "icon-reunion",
            value: this.meetingsTotalValue,
          })
        );
      }

      if (this.hasSimpleValue(this.physicalPerson?.honorariumDirectors)) {
        sections.push(
          this.buildSimpleSection({
            key: "HONORARIO_DIRETORIA",
            title: "Honorário Diretoria",
            entry: this.physicalPerson.honorariumDirectors,
          })
        );
      }

      (this.physicalPerson?.otherValues || []).forEach((item, index) => {
        if (!this.hasSimpleValue(item)) return;
        sections.push(
          this.buildSimpleSection({
            key: `OUTROS-${index}`,
            title: item.descriptionProduction || "Outros Valores",
            entry: item,
          })
        );
      });

      return sections;
    },

    hasMeetingsData() {
      return !!this.physicalPerson?.meetings?.length;
    },
    meetingsTotalValue() {
      if (!this.hasMeetingsData) return 0;
      return this.physicalPerson.meetings.reduce(
        (total, item) => total + this.toNumber(item.amountReceivable ?? item.grossValue),
        0
      );
    },
  },
  async mounted() {
    await this.initializeData();
  },
  methods: {
    // ---------- ciclo de vida / carregamento ----------
    goBack() {
      this.$router.go(-1);
    },
    toMoment(date) {
      if (!date) return null;
      return moment.isMoment(date) ? date : moment(date, "MM/YYYY");
    },
    async initializeData() {
      this.isInitializing = true;
      this.loadingPeriod = true;

      try {
        const competenceData = await getCompetences();
        const latestCompetence = competenceData?.[0]?.competence;

        this.maxDate = latestCompetence ? moment(latestCompetence, "MM/YYYY") : moment();
        this.selectedDate = latestCompetence ? moment(latestCompetence, "MM/YYYY") : moment();

        await this.fetchAnalyticsData();
      } catch (error) {
        console.error("Erro ao inicializar competências:", error);
      } finally {
        this.loadingPeriod = false;
        this.isInitializing = false;
      }
    },
    async onCompetenceChange(newDate) {
      if (this.isInitializing || !newDate) return;
      this.selectedDate = newDate;
      await this.fetchAnalyticsData();
    },
    async fetchAnalyticsData() {
      if (!this.selectedDate) return;

      this.loadingPeriod = true;
      try {
        const crm = this.$store.getters.getUserName || "";
        const response = await getAnalyticsAfterSelectingPeriod(
          crm,
          this.year,
          getMonthNumber(this.month)
        );

        if (!response) {
          this.clearData();
          return;
        }

        // Em alguns ambientes o payload vem dentro de `response.analytical`
        const data = response.analytical || response;

        this.physicalPerson = data?.physicalPerson || null;
        this.juridicalPerson = data?.juridicalPerson || [];
        this.transfer = data?.transfer || null;
        this.lastUpdateDate =
          response.dataAnalytical || moment().format("DD/MM/YYYY, HH:mm:ss");
      } catch (error) {
        console.error("Erro ao buscar dados analíticos:", error);
        this.clearData();
      } finally {
        this.loadingPeriod = false;
      }
    },
    clearData() {
      this.physicalPerson = null;
      this.juridicalPerson = [];
      this.transfer = null;
    },

    // ---------- helpers de normalização de dados ----------
    toNumber(value) {
      return parseFloat(value || 0) || 0;
    },
    hasSimpleValue(entry) {
      if (!entry) return false;
      return (
        this.toNumber(entry.invoiceValue) > 0 ||
        this.toNumber(entry.amountReceivable) > 0 ||
        this.toNumber(entry.grossValue) > 0
      );
    },
    hasMainData(entry, valueKeys = ["invoiceValue"]) {
      if (!entry || !entry.mainData) return false;
      const hasValue = valueKeys.some((key) => this.toNumber(entry.mainData[key]) > 0);
      return hasValue || !!entry.descriptionProduction;
    },
    formatCurrency(value) {
      return this.toNumber(value).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    // ---------- construção dos blocos exibidos em tela ----------
    buildMainSection({
      key,
      title,
      cardTitle,
      subtitle,
      entry,
      hasUploadInvoice,
      infoText,
      preferAmountReceivable = false,
    }) {
      const main = entry.mainData;
      const headlineValue = preferAmountReceivable
        ? main.amountReceivable ?? main.invoiceValue
        : main.invoiceValue;

      return {
        key,
        raw: entry,
        props: {
          title,
          cardTitle: cardTitle || title,
          cardSubtitle: subtitle,
          value: this.formatCurrency(headlineValue),
          icon: "icon-producao-medica",
          hasUploadInvoice,
          infoText,
          showSummaryCards: true,
          backbeatValue: this.formatCurrency(entry.backBeat),
          glosasValue: this.formatCurrency(entry.grosses),
        },
        statementData: {
          title: subtitle || title,
          invoiceValue: this.formatCurrency(main.invoiceValue ?? main.amountReceivable),
          grossValue: this.formatCurrency(main.grossValue),
          netValue: this.formatCurrency(main.amountReceivable),
          discounts: this.formatCurrency(main.discount),
          glosses: this.formatCurrency(entry.grosses),
        },
        invoiceData: hasUploadInvoice
          ? {
              providerName: subtitle || title,
              invoiceValue: this.formatCurrency(headlineValue),
            }
          : null,
      };
    },
    buildSimpleSection({ key, title, icon, entry, value }) {
      const headlineValue = value ?? entry?.invoiceValue ?? entry?.amountReceivable ?? 0;

      return {
        key,
        raw: entry,
        props: {
          title,
          cardTitle: title,
          value: this.formatCurrency(headlineValue),
          icon: icon || "icon-producao-medica",
          hasUploadInvoice: false,
          infoText: NO_INVOICE_TEXT,
          showSummaryCards: false,
        },
        statementData: {
          title,
          invoiceValue: this.formatCurrency(headlineValue),
          grossValue: this.formatCurrency(entry?.grossValue ?? headlineValue),
          netValue: this.formatCurrency(entry?.amountReceivable ?? headlineValue),
          discounts: "0,00",
          glosses: "0,00",
        },
        invoiceData: null,
      };
    },

    // ---------- ações dos cards ----------
    openStatementModal(section) {
      this.selectedStatementData = section.statementData;
      this.isStatementModalOpen = true;
    },
    openInvoiceUploadModal(section) {
      if (!section.invoiceData) return;
      this.selectedInvoiceData = section.invoiceData;
      this.isInvoiceModalOpen = true;
    },
    openBackbeat() {
      showBackbeat(this.year, this.month);
    },
    openGlosas() {
      this.$router.push({
        path: RoutesPaths.financial.reviewGlosses(),
        query: { year: this.year, month: this.month },
        replace: true,
      });
    },

    // ---------- histórico ----------
    handleDownloadStatement(payload) {
      if (payload) console.log("Download de demonstrativo:", payload);
    },
    handleInvoiceUploadSuccess(payload) {
      if (payload) console.log("Nota enviada com sucesso:", payload);
      this.fetchAnalyticsData();
    },
    handleDownloadInvoice(item) {
      if (item) console.log("Download de NF:", item);
    },
    handleDeleteInvoice(item) {
      if (item) console.log("Excluir NF:", item);
    },
  },
};
</script>

<style lang="scss" scoped>
.production-management-page {
  font-family: "Trebuchet MS", sans-serif;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 16px 80px 16px;

  .divider-line {
    border-bottom: 1px solid #dfdfdf;
    width: 100%;
  }

  .empty-state {
    text-align: center;
    color: #6c757d;
    padding: 24px 0;
  }
}
</style>